import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { refreshAccessTokenRequest } from '../services/api';
import { DEBUG_SKIP_AUTH } from '../config/debug';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    setUser(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    delete api.defaults.headers.common.Authorization;
  }, []);

  const applySession = useCallback((newAccessToken, newRefreshToken, userData) => {
    setUser(userData);
    localStorage.setItem('accessToken', newAccessToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
  }, []);

  const fetchUserWithToken = useCallback(async (token) => {
    const response = await api.get('/api/auth/verify', {
      headers: { Authorization: `Bearer ${token}` },
      skipAuthRefresh: true,
    });
    return response.data.user;
  }, []);

  const debugLogin = useCallback(async () => {
    const response = await api.post(
      '/api/auth/debug-login',
      {},
      { skipAuthRefresh: true }
    );
    const {
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
      user: userData,
    } = response.data;
    applySession(newAccessToken, newRefreshToken, userData);
    return userData;
  }, [applySession]);

  // Run once on mount — do not re-run when tokens change (avoids refresh loops)
  useEffect(() => {
    let cancelled = false;

    async function bootstrapAuth() {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      if (!accessToken && DEBUG_SKIP_AUTH) {
        try {
          await debugLogin();
          if (!cancelled) setLoading(false);
          return;
        } catch (error) {
          console.warn('Debug auth failed, falling back to normal login:', error);
        }
      }

      if (!accessToken) {
        if (!cancelled) setLoading(false);
        return;
      }

      api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

      try {
        const userData = await fetchUserWithToken(accessToken);
        if (!cancelled) setUser(userData);
      } catch (error) {
        const code = error.response?.data?.code;
        const shouldTryRefresh =
          refreshToken &&
          (code === 'TOKEN_EXPIRED' || error.response?.status === 401);

        if (shouldTryRefresh) {
          try {
            const newAccessToken = await refreshAccessTokenRequest();
            const userData = await fetchUserWithToken(newAccessToken);
            if (!cancelled) setUser(userData);
          } catch (refreshError) {
            console.error('Session refresh failed:', refreshError);
            if (DEBUG_SKIP_AUTH) {
              try {
                await debugLogin();
                if (!cancelled) setLoading(false);
                return;
              } catch (debugError) {
                console.warn('Debug auth retry failed:', debugError);
              }
            }
            clearSession();
          }
        } else {
          if (DEBUG_SKIP_AUTH) {
            try {
              clearSession();
              await debugLogin();
              if (!cancelled) setLoading(false);
              return;
            } catch (debugError) {
              console.warn('Debug auth retry failed:', debugError);
            }
          }
          clearSession();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    bootstrapAuth();

    return () => {
      cancelled = true;
    };
  }, [applySession, clearSession, debugLogin, fetchUserWithToken]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/api/auth/login', {
        email,
        password,
      });

      const {
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
        user: userData,
      } = response.data;

      applySession(newAccessToken, newRefreshToken, userData);

      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
      };
    }
  };

  const logout = async () => {
    const storedRefresh = localStorage.getItem('refreshToken');
    if (storedRefresh) {
      try {
        await api.post(
          '/api/auth/logout',
          { refreshToken: storedRefresh },
          { skipAuthRefresh: true }
        );
      } catch (error) {
        console.error('Failed to revoke refresh token:', error);
      }
    }

    clearSession();
  };

  const value = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
